<template>
  <div class="modal fade fixed-end dl-drawer" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5" role="document">
      <div class="modal-content">

        <div class="dl-header">
          <div class="dl-header-text">
            <h2>
              US-{{ storyId }}
              <i class="fas fa-exchange-alt dl-header-icon"></i>
              Doc #{{ payload && payload.doc.id }}
            </h2>
            <p v-if="payload">{{ payload.doc.doc_project_name }} · user story doc</p>
          </div>
          <button type="button" class="dl-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="far fa-times"></i>
          </button>
        </div>

        <div class="dl-id-pair" v-if="payload || loading">
          <div class="dl-id-card">
            <div class="dl-id-label">SCRUM BOARD</div>
            <div class="dl-id-row">
              <span class="dl-id-number">
                <template v-if="loading"><span class="skeleton-line w-25 d-inline-block"></span></template>
                <template v-else>US-{{ payload.board.id }}</template>
              </span>
              <button v-if="!loading" type="button" class="dl-copy-btn" title="Copy ID" @click="copyId(`US-${payload.board.id}`)">
                <i class="far fa-copy"></i>
              </button>
            </div>
            <div class="dl-id-meta">
              <template v-if="loading"><span class="skeleton-line w-50 d-inline-block"></span></template>
              <template v-else>{{ payload.board.status }} · edited by {{ payload.board.edited_by ? payload.board.edited_by.name : '—' }}, {{ payload.board.edited_at | fromNow }}</template>
            </div>
          </div>

          <div class="dl-id-link"><i class="far fa-link"></i></div>

          <div class="dl-id-card">
            <div class="dl-id-label">USER STORY DOC</div>
            <div class="dl-id-row">
              <span class="dl-id-number">
                <template v-if="loading"><span class="skeleton-line w-25 d-inline-block"></span></template>
                <template v-else>#{{ payload.doc.id }}</template>
              </span>
              <button v-if="!loading" type="button" class="dl-copy-btn" title="Copy ID" @click="copyId(`#${payload.doc.id}`)">
                <i class="far fa-copy"></i>
              </button>
              <router-link
                v-if="!loading && payload.doc.doc_stage_id"
                class="dl-open-link"
                :to="{ name: 'projects.stage2.userstories', params: { project_id: $route.params.project_id, stage_id: payload.doc.doc_stage_id } }"
                target="_blank"
              >
                Open in doc <i class="far fa-external-link-alt"></i>
              </router-link>
            </div>
            <div class="dl-id-meta">
              <template v-if="loading"><span class="skeleton-line w-50 d-inline-block"></span></template>
              <template v-else>edited by {{ payload.doc.edited_by ? payload.doc.edited_by.name : '—' }}, {{ payload.doc.edited_at | fromNow }}</template>
            </div>
          </div>
        </div>

        <div class="dl-status" :class="{ 'dl-status--drift': payload && !payload.in_sync }">
          <template v-if="loading">
            <span class="skeleton-line w-75 d-inline-block"></span>
          </template>
          <template v-else-if="payload">
            <template v-if="payload.in_sync">
              <i class="fas fa-check-circle dl-status-icon dl-status-icon--ok"></i>
              Board and doc match.
            </template>
            <template v-else>
              <i class="fas fa-exclamation-triangle dl-status-icon dl-status-icon--drift"></i>
              Differs in {{ differingFieldLabels }}.
            </template>
          </template>
        </div>

        <div v-if="error" class="dl-error">
          <p>Unable to load doc link data.</p>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="load">Try again</button>
        </div>

        <div v-else class="dl-diff">
          <template v-if="loading">
            <div v-for="i in 4" :key="i" class="dl-field-skeleton">
              <div class="skeleton-line w-25 mb-2"></div>
              <div class="skeleton-line w-100 mb-1"></div>
              <div class="skeleton-line w-75"></div>
            </div>
          </template>

          <template v-else-if="payload">
            <div class="story-history-sheet">
              <div class="history-toolbar">
                <div v-if="mode !== 'clean'" class="form-check history-collapse">
                  <input :id="`dl-collapse-${_uid}`" class="form-check-input" type="checkbox" :checked="collapse" @change="setCollapse($event.target.checked)" />
                  <label class="form-check-label" :for="`dl-collapse-${_uid}`">Collapse unchanged</label>
                </div>
                <div class="btn-group history-modes" role="group" aria-label="Diff view mode">
                  <input id="dl-mode-inline" type="radio" class="btn-check" name="dl-mode" autocomplete="off" :checked="mode === 'inline'" @change="setMode('inline')" />
                  <label class="btn btn-outline-primary btn-sm" for="dl-mode-inline">Inline</label>
                  <input id="dl-mode-split" type="radio" class="btn-check" name="dl-mode" autocomplete="off" :checked="mode === 'split'" @change="setMode('split')" />
                  <label class="btn btn-outline-primary btn-sm" for="dl-mode-split">Side by side</label>
                  <input id="dl-mode-clean" type="radio" class="btn-check" name="dl-mode" autocomplete="off" :checked="mode === 'clean'" @change="setMode('clean')" />
                  <label class="btn btn-outline-primary btn-sm" for="dl-mode-clean">Clean</label>
                </div>
              </div>

              <template v-if="payload.in_sync">
                <p class="dl-no-diff">No differences between the board story and the doc.</p>
              </template>
              <template v-else>
                <div class="dl-legend">
                  <span class="dl-legend-swatch dl-legend-swatch--removed"></span> Board US-{{ payload.board.id }}
                  <span class="dl-legend-swatch dl-legend-swatch--added dl-legend-swatch--gap"></span> Doc #{{ payload.doc.id }}
                </div>
                <field-diff
                  v-for="field in diffFields"
                  :key="field.key"
                  :field="field"
                  :mode="mode"
                  :collapse="collapse"
                  :from-label="`Board US-${payload.board.id}`"
                  :to-label="`Doc #${payload.doc.id}`"
                />
              </template>
            </div>
          </template>
        </div>

        <div class="dl-footer">
          <template v-if="!loading && payload && !payload.in_sync">
            <button
              type="button"
              class="btn btn-primary dl-action-btn"
              :disabled="syncing"
              @click="syncFromDoc"
            >
              <i class="far" :class="syncing ? 'fa-spinner fa-spin' : 'fa-arrow-down'"></i>
              Update board from doc
            </button>
          </template>
          <template v-else-if="!loading && payload && payload.in_sync">
            <span class="dl-footer-nothing">Nothing to update.</span>
          </template>
          <button type="button" class="btn btn-outline-secondary dl-action-btn" data-bs-dismiss="modal">
            Close
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { http } from 'utils'
import { toast, CModal } from '@chameleon/chameleon-vue'
import { mapMutations } from 'vuex'
import { UPDATE_LIST_ITEM } from 'vx/mutations'
import { diffStates } from 'utils/story-history'
import FieldDiff from 'components/user-stories/history/FieldDiff.vue'

const MODE_KEY = 'userstory-history.mode'
const COLLAPSE_KEY = 'userstory-history.collapse'

const readPreference = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key)
    return value === null ? fallback : JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

const writePreference = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {}
}

export default {
  extends: CModal,

  components: { FieldDiff },

  filters: {
    fromNow (value) {
      return value ? moment(value).fromNow() : '—'
    }
  },

  data () {
    return {
      storyId: null,
      userStoryDocId: null,
      payload: null,
      loading: false,
      error: false,
      syncing: false,
      mode: 'inline',
      collapse: true,
    }
  },

  computed: {
    diffFields () {
      if (!this.payload || this.payload.in_sync) return []
      return diffStates(this.payload.board.fields, this.payload.doc.fields)
    },

    differingFieldLabels () {
      if (!this.payload) return ''
      const labelMap = {
        title: 'story statement',
        description: 'acceptance criteria',
        tech_notes: 'technical notes',
        test_notes: 'testing notes',
      }
      const labels = this.payload.differing_fields.map(f => labelMap[f] || f)
      if (labels.length <= 1) return labels[0] || ''
      return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    },
  },

  mounted () {
    this.addEventListener('open', () => {
      this.mode = readPreference(MODE_KEY, 'inline')
      this.collapse = readPreference(COLLAPSE_KEY, true)
      this.load()
    })
  },

  methods: {
    ...mapMutations('yodizproject/stories', [UPDATE_LIST_ITEM]),

    load () {
      this.loading = true
      this.error = false
      this.payload = null

      return http.get(`/userstorydocs/${this.userStoryDocId}/stories/${this.storyId}/doc-link`)
        .then((response) => {
          this.payload = response.data
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.error = true
        })
    },

    syncFromDoc () {
      this.syncing = true

      return http.post(`/userstorydocs/${this.userStoryDocId}/stories/${this.storyId}/sync-from-doc`)
        .then((response) => {
          this[UPDATE_LIST_ITEM]([response.data])
          toast(`US-${this.storyId} updated from doc`, 'success')
          this.payload = { ...this.payload, in_sync: true, differing_fields: [] }
        })
        .catch(() => {
          toast('Update failed', 'danger')
        })
        .finally(() => {
          this.syncing = false
        })
    },

    setMode (mode) {
      this.mode = mode
      writePreference(MODE_KEY, mode)
    },

    setCollapse (collapse) {
      this.collapse = collapse
      writePreference(COLLAPSE_KEY, collapse)
    },

    copyId (text) {
      navigator.clipboard.writeText(text).then(() => {
        toast(`${text} copied`, 'success')
      }).catch(() => {
        toast('Unable to copy', 'danger')
      })
    },
  }
}
</script>

<style scoped>
.dl-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #E7E9EC;
  gap: 12px;
}

.dl-header h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #1F1F1F;
}

.dl-header p {
  font-size: 12px;
  color: #6B7280;
  margin: 0;
}

.dl-header-icon {
  font-size: 12px;
  color: #9AA1A9;
  margin: 0 4px;
}

.dl-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: #6B7280;
  font-size: 16px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dl-close:hover {
  background: #F3F6FA;
  color: #1F1F1F;
}

.dl-id-pair {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  padding: 16px 20px;
  background: #FAFBFC;
  border-bottom: 1px solid #E7E9EC;
  align-items: center;
}

.dl-id-card {
  background: #fff;
  border: 1px solid #E7E9EC;
  border-radius: 6px;
  padding: 10px 12px;
}

.dl-id-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6B7280;
  margin-bottom: 4px;
}

.dl-id-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.dl-id-number {
  font-size: 15px;
  font-weight: 700;
  color: #1F1F1F;
}

.dl-id-meta {
  font-size: 11.5px;
  color: #6B7280;
  margin-top: 4px;
}

.dl-id-link {
  font-size: 14px;
  color: #9AA1A9;
  text-align: center;
}

.dl-copy-btn {
  border: none;
  background: none;
  color: #6B7280;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  line-height: 1;
}

.dl-copy-btn:hover {
  color: #0B5DB4;
}

.dl-open-link {
  font-size: 12px;
  font-weight: 500;
  color: #0B5DB4;
  text-decoration: none;
  margin-left: auto;
  white-space: nowrap;
}

.dl-open-link:hover {
  text-decoration: underline;
}

.dl-status {
  padding: 10px 20px;
  font-size: 12.5px;
  color: #1F1F1F;
  border-bottom: 1px solid #E7E9EC;
}

.dl-status--drift {
  background: #FFF7E8;
  color: #5E3A00;
}

.dl-status-icon {
  margin-right: 6px;
}

.dl-status-icon--ok {
  color: #2DA748;
}

.dl-status-icon--drift {
  color: #D98A00;
}

.dl-error {
  padding: 20px;
  color: #6B7280;
  font-size: 13px;
}

.dl-diff {
  flex: 1;
  overflow: auto;
  padding: 16px 20px 24px;
}

.dl-no-diff {
  font-size: 13px;
  color: #6B7280;
  margin: 0;
}

.dl-legend {
  font-size: 11.5px;
  color: #6B7280;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.dl-legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.dl-legend-swatch--removed {
  background: #FCE9EB;
  border: 1px solid #F4C2C8;
}

.dl-legend-swatch--added {
  background: #E3F4E8;
  border: 1px solid #B8E3C5;
}

.dl-legend-swatch--gap {
  margin-left: 10px;
}

.dl-field-skeleton {
  margin-bottom: 16px;
}

.dl-footer {
  padding: 12px 20px;
  border-top: 1px solid #E7E9EC;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dl-action-btn {
  height: 36px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
}

.dl-footer-nothing {
  font-size: 11.5px;
  color: #6B7280;
}

.skeleton-line {
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, #EEF0F3 25%, #F6F7F9 50%, #EEF0F3 75%);
  background-size: 200% 100%;
  animation: history-skeleton 1.2s ease-in-out infinite;
}
</style>
